import { ConfigField } from './ConfigField';
import { ReviewQuestionToggleField } from './ReviewQuestionToggleField';

export interface SubQuestionRequirementToggleProps {
  /** Visible label above the switch. */
  readonly label: string;
  /** Info-tooltip help text shown next to the label. */
  readonly info: string;
  /** Accessible label for the info-icon trigger. */
  readonly infoAltText: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  /** Tracking id for the shared Toggle (field-scoped, e.g. `…require-comment-on-fail`). */
  readonly trackingId: string;
  readonly disabled: boolean;
  readonly dataTestId?: string;
}

/**
 * One sub-question requirement toggle rendered in the same label-on-top +
 * info-tooltip style as the Question Configuration step's toggles: a
 * {@link ConfigField} label row above a {@link ReviewQuestionToggleField}
 * switch (external-label mode, wired via the ConfigField's label id).
 *
 * Shared by {@link SubQuestionRow} (the four scored Pass/Fail toggles) and
 * {@link SubQuestionDataCollectionFields} (the two unconditional
 * comment/attachment toggles) so both render identically to the config step
 * from one place.
 */
export function SubQuestionRequirementToggle({
  label,
  info,
  infoAltText,
  checked,
  onChange,
  trackingId,
  disabled,
  dataTestId,
}: SubQuestionRequirementToggleProps): JSX.Element {
  return (
    <ConfigField
      label={label}
      info={info}
      infoAltText={infoAltText}
      renderLabel={({ labelId }) => (
        <ReviewQuestionToggleField
          checked={checked}
          onChange={onChange}
          trackingId={trackingId}
          labelId={labelId}
          disabled={disabled}
          {...(dataTestId !== undefined ? { dataTestId } : {})}
        />
      )}
    />
  );
}
